import React from 'react';
import {
    Card,
    IconButton,
    IconButtonGroup,
    LoadingOverlay,
} from '@travelity/ui';
import { Ota, ResourceType, useUpdateOtaStatus } from '@travelity/api';
import { Stack, Switch, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import VisibilityIcon from '@mui/icons-material/Visibility';
import OtaCardSkeleton from './ota-card-skeleton';
import { otaLogos } from '../otas.constants';
import { useResourcePermissions } from '../../../contexts/user';

export interface OtaCardProps {
    ota: Ota;
}

export const OtaCard = (props: OtaCardProps) => {
    const { ota } = props;
    const { t } = useTranslation();
    const permissions = useResourcePermissions(ResourceType.PRODUCT_VARIANT);

    const { mutate: updateStatus, isLoading: isUpdating } =
        useUpdateOtaStatus();

    return (
        <Card
            parentProps={{ maxWidth: '320px' }}
            buttons={
                <IconButtonGroup disabled={!ota.enabled}>
                    <IconButton
                        disabled={!permissions.read}
                        href={`/otas/${ota.id}`}
                        icon={<VisibilityIcon fontSize="small" />}
                        tooltip="Preview"
                    />
                </IconButtonGroup>
            }
        >
            <Stack p={2}>
                <Stack
                    direction="row"
                    alignItems="center"
                    gap={1}
                    justifyContent="flex-end"
                >
                    <Switch
                        disabled={isUpdating || !permissions.create}
                        checked={ota.enabled}
                        onChange={(e, activate) =>
                            updateStatus({ id: ota.id, activate })
                        }
                        sx={{
                            '&& .MuiSwitch-track': {
                                '&:before': {
                                    left: '10px',
                                    content: `"${t('On')}"`,
                                },
                                '&:after': {
                                    right: '12px',
                                    content: `"${t('Off')}"`,
                                },
                            },
                        }}
                    />
                </Stack>
                <Stack
                    direction="row"
                    gap={2}
                    alignItems="center"
                    height="100px"
                >
                    <img src={otaLogos[ota.id]} alt={ota.name} />
                    <Typography>{ota.name}</Typography>
                </Stack>
            </Stack>
            {isUpdating && <LoadingOverlay title="Updating OTA" />}
        </Card>
    );
};

OtaCard.Skeleton = OtaCardSkeleton;
